import { useEffect, useState } from "react";
import { ArrowLeft, Crown, Coins, Loader2, Calendar, Receipt, Sparkles, XCircle, Gift, Copy, Package } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
};

export const BillingPage = ({ user, onBack, onUpgrade, onRefreshUser }) => {
  const [data, setData] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [copied, setCopied] = useState(false);
  const [promo, setPromo] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const referralLink = user?.referral_code ? `${window.location.origin}/?ref=${user.referral_code}` : "";
  const copyRef = () => {
    navigator.clipboard?.writeText(referralLink);
    setCopied(true);
    toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const load = () => api.billing().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);

  const redeem = async () => {
    if (!promo.trim()) return;
    setRedeeming(true);
    try {
      const res = await api.redeemPromo(promo.trim());
      toast.success(`Code applied — you got ${res.reward}!`);
      setPromo("");
      await load();
      onRefreshUser && onRefreshUser();
    } catch (e) {
      toast.error(apiError(e, "That code isn't valid."));
    } finally {
      setRedeeming(false);
    }
  };

  const cancel = async () => {
    setCanceling(true);
    try {
      await api.cancelSubscription();
      toast.success("Subscription will end at the close of your billing period.");
      await load();
    } catch (e) {
      toast.error(apiError(e, "Could not cancel subscription."));
    } finally {
      setCanceling(false);
    }
  };

  if (!data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  const subscribed = data.subscription_active;

  return (
    <div data-testid="billing-page" className="max-w-4xl mx-auto py-8">
      <button data-testid="billing-back" onClick={onBack}
        className="flex items-center gap-2 font-ui text-sm text-slate-400 hover:text-gold-200 transition-colors mb-8">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <h1 className="font-display text-4xl text-slate-100 mb-2">Account & Billing</h1>
      <p className="font-read text-slate-400 mb-8">Manage your plan, credits and payment history.</p>

      {/* Plan card */}
      <div className="grid md:grid-cols-2 gap-5 mb-6">
        <div className={`rounded-2xl p-6 border ${subscribed ? "border-gold-300/60 bg-gradient-to-b from-gold-400/10 to-transparent" : "border-gold-500/12 bg-navy-900/40"}`}>
          <div className="flex items-center gap-2 mb-2">
            {subscribed ? <Crown className="w-5 h-5 text-slate-300" /> : <Sparkles className="w-5 h-5 text-slate-400" />}
            <span className="font-ui text-xs uppercase tracking-widest text-slate-300">Current plan</span>
          </div>
          <div data-testid="billing-plan" className="font-display text-3xl text-slate-100">
            {subscribed ? "Unlimited" : "Pay per book"}
          </div>
          {subscribed ? (
            <div className="mt-3 font-read text-sm text-slate-400 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {data.subscription_cancel_at_period_end
                ? `Ends ${fmtDate(data.subscription_expires)} — won't renew`
                : `Renews around ${fmtDate(data.subscription_expires)}`}
            </div>
          ) : (
            <p className="mt-3 font-read text-sm text-slate-400">
              {data.free_books_left > 0 ? `${data.free_books_left} free preview${data.free_books_left > 1 ? "s" : ""} left. ` : ""}$45 per complete book, down to $35 each when you buy 18 or more.
            </p>
          )}
          <div className="mt-5 flex flex-wrap gap-3">
            {!subscribed && (
              <button data-testid="billing-upgrade" onClick={onUpgrade}
                className="klyr-btn-metal font-ui text-sm font-semibold rounded-xl px-5 py-2.5 transition-[filter]">
                Buy book credits
              </button>
            )}
            {subscribed && !data.subscription_cancel_at_period_end && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid="billing-cancel" disabled={canceling}
                    className="flex items-center gap-2 font-ui text-sm text-red-300 border border-red-500/40 hover:border-red-400 rounded-xl px-5 py-2.5 transition-colors">
                    {canceling ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Cancel subscription
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-navy-950 border-gold-500/12 text-slate-100">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel your Unlimited plan?</AlertDialogTitle>
                    <AlertDialogDescription className="text-slate-400">
                      You'll keep unlimited access until {fmtDate(data.subscription_expires)}. After that your account
                      returns to pay-per-book. You can resubscribe anytime.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-navy-800 border-gold-500/12 text-slate-200 hover:bg-navy-700">Keep plan</AlertDialogCancel>
                    <AlertDialogAction data-testid="billing-cancel-confirm" onClick={cancel}
                      className="bg-red-600 hover:bg-red-500 text-white">Cancel at period end</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        {/* Credits card */}
        <div className="rounded-2xl p-6 border border-gold-500/12 bg-navy-900/40">
          <div className="flex items-center gap-2 mb-2">
            <Coins className="w-5 h-5 text-emerald-400" />
            <span className="font-ui text-xs uppercase tracking-widest text-emerald-300">Book credits</span>
          </div>
          <div data-testid="billing-credits" className="font-display text-3xl text-slate-100">{data.credits}</div>
          <p className="mt-3 font-read text-sm text-slate-400">
            {subscribed ? "Credits pause while you're on Unlimited." : "Each credit makes one complete book with its Amazon KDP package. Credits never expire."}
          </p>
          {(user?.publish_credits || 0) > 0 && (
            <div data-testid="billing-publish-credits" className="mt-3 flex items-center gap-2 font-ui text-sm text-slate-300">
              <Package className="w-4 h-4" /> {user.publish_credits} publish credit{user.publish_credits > 1 ? "s" : ""} available
            </div>
          )}
          <button data-testid="billing-buy-credits" onClick={onUpgrade}
            className="mt-5 font-ui text-sm text-slate-200 border border-gold-500/12 hover:border-gold-300 rounded-xl px-5 py-2.5 transition-colors">
            Buy credits
          </button>
        </div>
      </div>

      {/* Refer & earn */}
      <div data-testid="billing-referral" className="rounded-2xl border border-gold-400/30 bg-gradient-to-b from-gold-400/8 to-transparent p-6 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Gift className="w-5 h-5 text-slate-300" />
          <span className="font-ui text-xs uppercase tracking-widest text-slate-300">Refer &amp; earn</span>
        </div>
        <h2 className="font-display text-2xl text-slate-100">Give a friend, get a free book</h2>
        <p className="mt-2 font-read text-sm text-slate-400">
          Share your link. When a friend signs up and finishes their first book, you get <span className="text-slate-200 font-medium">1 free book credit</span> — automatically.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px] font-mono2 text-sm text-slate-200 bg-navy-950/70 border border-gold-500/12 rounded-lg px-4 py-2.5 truncate">
            {referralLink || "—"}
          </div>
          <button data-testid="copy-referral" onClick={copyRef} disabled={!referralLink}
            className="flex items-center gap-2 klyr-btn-metal font-ui text-sm font-semibold rounded-lg px-4 py-2.5 disabled:opacity-50 transition-[filter]">
            <Copy className="w-4 h-4" /> {copied ? "Copied" : "Copy link"}
          </button>
        </div>
        <div className="mt-4 font-ui text-sm text-slate-400">
          Friends referred so far: <span data-testid="referral-count" className="text-slate-200 font-semibold">{user?.referral_count || 0}</span>
        </div>
      </div>

      {/* Redeem a code */}
      <div data-testid="redeem-card" className="rounded-2xl border border-gold-500/12 bg-navy-900/40 p-6 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-emerald-400" />
          <span className="font-ui text-xs uppercase tracking-widest text-emerald-300">Have a code?</span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input data-testid="redeem-input" value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())}
            placeholder="Enter promo code" onKeyDown={(e) => e.key === "Enter" && redeem()}
            className="flex-1 min-w-[200px] bg-navy-950/70 border border-gold-500/12 focus:border-gold-400/60 outline-none rounded-lg px-4 py-2.5 font-mono2 text-sm text-slate-100 placeholder:text-slate-500" />
          <button data-testid="redeem-button" onClick={redeem} disabled={redeeming || !promo.trim()}
            className="flex items-center gap-2 klyr-btn-metal disabled:opacity-50 font-ui text-sm font-semibold rounded-lg px-5 py-2.5 transition-[filter]">
            {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Redeem
          </button>
        </div>
      </div>

      {/* History */}
      <div className="rounded-2xl border border-gold-500/12 bg-navy-900/40 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Receipt className="w-5 h-5 text-slate-300" />
          <h2 className="font-ui text-lg font-semibold text-slate-100">Payment history &amp; receipts</h2>
        </div>
        <p data-testid="billing-receipts-note" className="font-read text-xs text-slate-500 -mt-2 mb-4">
          A receipt is emailed to you after every purchase. Every paid order is listed here; your finished books and their downloads stay in My Library.
        </p>
        {data.history.length === 0 ? (
          <p data-testid="billing-history-empty" className="font-read text-sm text-slate-500 py-6 text-center">
            No payments yet.
          </p>
        ) : (
          <div className="divide-y divide-gold-500/12">
            {data.history.map((h, i) => (
              <div key={i} data-testid={`billing-txn-${i}`} className="flex items-center justify-between py-3">
                <div>
                  <div className="font-ui text-sm text-slate-200">{h.item}</div>
                  <div className="font-ui text-xs text-slate-500">{fmtDate(h.date)}</div>
                </div>
                <div className="font-ui text-sm font-semibold text-slate-100">${h.amount.toFixed(2)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
